import {useEffect, useState} from "react";
import {useAuth} from "@/context/use-auth";
import {
    createBillingUnitConfig,
    getBillingUnitConfigHistory,
    getCurrentBillingUnitConfig,
} from "@/services/billing-unit-config-services";
import {BillingUnitConfigCurrent, BillingUnitConfigHistoryEntry} from "@/models/billing-unit-config";
import {Button} from "@/components/ui/button";
import {useToast} from "@/hooks/use-toast";
import {Check, ChevronDown, ChevronUp, Pencil} from "lucide-react";
import {format, parseISO} from "date-fns";
import {de} from "date-fns/locale";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {useAdminTranslation} from "@/i18n/hooks";
import {Text} from "@/components/tailwind/text.tsx";
import log from '@/lib/logger';

export default function SectionBillingUnitSettings() {
    const {token} = useAuth();
    const {toast} = useToast();
    const {t} = useAdminTranslation();
    const [current, setCurrent] = useState<BillingUnitConfigCurrent | null>(null);
    const [history, setHistory] = useState<BillingUnitConfigHistoryEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [selectedUnitMinutes, setSelectedUnitMinutes] = useState<30 | 60>(60);

    useEffect(() => {
        loadData();
    }, [token]);

    async function loadData() {
        setIsLoading(true);
        try {
            const currentData = await getCurrentBillingUnitConfig(token);
            if (currentData) {
                setCurrent(currentData);
                if (!currentData.featureEnabled) {
                    setIsLoading(false);
                    return;
                }
            }
            const historyData = await getBillingUnitConfigHistory(token);
            if (historyData) setHistory(historyData);
        } catch (error) {
            log.error("Error fetching billing unit config:", error);
        } finally {
            setIsLoading(false);
        }
    }

    function startEditing() {
        setSelectedUnitMinutes(current?.unitMinutes === 30 ? 30 : 60);
        setIsEditing(true);
    }

    function cancelEditing() {
        setIsEditing(false);
    }

    async function save() {
        setIsSaving(true);
        try {
            await createBillingUnitConfig(token, selectedUnitMinutes);
            toast({
                title: t("adminSettings.billingUnitSettings.toast.saveSuccess.title"),
                description: t("adminSettings.billingUnitSettings.toast.saveSuccess.description"),
            });
            setIsEditing(false);
            await loadData();
        } catch (error) {
            log.error("Error saving billing unit config:", error);
            toast({
                title: t("adminSettings.billingUnitSettings.toast.saveError.title"),
                description: t("adminSettings.billingUnitSettings.toast.saveError.description"),
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    }

    function formatDate(dateString: string) {
        return format(parseISO(dateString), "dd.MM.yyyy, HH:mm", {locale: de});
    }

    function formatUnitMinutes(unitMinutes: number) {
        return unitMinutes === 30
            ? t("adminSettings.billingUnitSettings.unit.halfHour")
            : t("adminSettings.billingUnitSettings.unit.hour");
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold">
                        {t("adminSettings.billingUnitSettings.title")}
                    </CardTitle>

                    {!isEditing && current?.featureEnabled && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={startEditing}
                            className="flex items-center"
                            aria-label={t("adminSettings.billingUnitSettings.button.edit.ariaLabel")}
                            data-testid="edit-billing-unit-button"
                        >
                            <Pencil className="h-4 w-4 mr-1"/>
                            {t("adminSettings.billingUnitSettings.button.edit.text")}
                        </Button>
                    )}
                </div>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {t("adminSettings.billingUnitSettings.loading")}
                    </div>
                ) : isEditing ? (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Text className="text-sm font-medium">
                                {t("adminSettings.billingUnitSettings.form.unitLabel")}
                            </Text>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="unitMinutes"
                                        value="60"
                                        checked={selectedUnitMinutes === 60}
                                        onChange={() => setSelectedUnitMinutes(60)}
                                        data-testid="unit-60-radio"
                                    />
                                    <span className="text-sm dark:text-gray-200">
                                        {t("adminSettings.billingUnitSettings.unit.hour")}
                                    </span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="unitMinutes"
                                        value="30"
                                        checked={selectedUnitMinutes === 30}
                                        onChange={() => setSelectedUnitMinutes(30)}
                                        data-testid="unit-30-radio"
                                    />
                                    <span className="text-sm dark:text-gray-200">
                                        {t("adminSettings.billingUnitSettings.unit.halfHour")}
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="flex space-x-2">
                            <Button
                                size="sm"
                                onClick={save}
                                disabled={isSaving}
                                className="flex items-center"
                                data-testid="save-billing-unit-button"
                            >
                                <Check className="h-4 w-4 mr-1"/>
                                {isSaving
                                    ? t("adminSettings.billingUnitSettings.button.save.saving")
                                    : t("adminSettings.billingUnitSettings.button.save.save")}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEditing}
                                disabled={isSaving}
                            >
                                {t("adminSettings.billingUnitSettings.button.cancel")}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <span className="text-2xl font-medium" data-testid="billing-unit-display">
                                {current
                                    ? formatUnitMinutes(current.unitMinutes)
                                    : t("adminSettings.billingUnitSettings.unit.hour")}
                            </span>
                            {current?.isDefault && (
                                <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {t("adminSettings.billingUnitSettings.defaultHint")}
                                </Text>
                            )}
                        </div>

                        <Text className="text-xs text-gray-500 dark:text-gray-400">
                            {t("adminSettings.billingUnitSettings.description")}
                        </Text>

                        {history.length > 0 && (
                            <div className="mt-4">
                                <button
                                    onClick={() => setShowHistory(!showHistory)}
                                    className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                                    data-testid="toggle-history-button"
                                >
                                    {showHistory ? (
                                        <ChevronUp className="h-4 w-4"/>
                                    ) : (
                                        <ChevronDown className="h-4 w-4"/>
                                    )}
                                    {t("adminSettings.billingUnitSettings.history.toggle", {count: history.length})}
                                </button>

                                {showHistory && (
                                    <div className="mt-2 space-y-2" data-testid="history-list">
                                        {history.map((entry) => (
                                            <div
                                                key={entry.id}
                                                className="text-xs text-gray-600 dark:text-gray-400 border-l-2 border-gray-200 dark:border-gray-700 pl-3 py-1"
                                            >
                                                <span className="font-medium dark:text-gray-200">
                                                    {formatUnitMinutes(entry.unitMinutes)}
                                                </span>
                                                {" "}–{" "}
                                                {t("adminSettings.billingUnitSettings.history.createdBy", {
                                                    name: entry.createdByName,
                                                    date: formatDate(entry.createdAt),
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
